import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { ReplaySubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import User from 'entity/User';
import { Presentation, PresentationSettings, UpdateType } from 'messages/websocket.messages';

import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { selectors } from '../../state/app';

import { AudioPresetButtonConfig, AUDIO_PRESET_BUTTONS } from './audio-settings.types';

@Component({
  selector: 'app-audio-settings',
  templateUrl: './audio-settings.component.html',
  styleUrls: ['./audio-settings.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioSettingsComponent extends Loggable implements OnInit, OnDestroy {
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);

  public readonly social = input<Social>();
  public settingsFormState?: PresentationSettings;
  public presetSelected?: AudioPresetButtonConfig;

  private user?: User;
  private presentationSettings?: PresentationSettings;
  private isCrossRoom = false;
  protected groupConversationsV1 = false;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('AudioSettingsComponent');

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.group_conversations_v1)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupConversationsV1) => {
        this.groupConversationsV1 = groupConversationsV1;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnInit(): void {
    this.store
      .select(selectors.getPresentationSettings)
      .pipe(
        filter((settings) => !!settings),
        takeUntil(this.destroyed$),
      )
      .subscribe((presentationSettings) => {
        this.handleSettingsUpdate(presentationSettings);
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(selectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => {
        this.isCrossRoom = isCrossRoom;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public async updateSettings(): Promise<void> {
    const { state } = SocialStateMachine.shared;
    if (!this.user) {
      this.error('updateSettings: User is undefined');
      return;
    }
    if (!state) {
      this.error('updateSettings: state is undefined');
      return;
    }
    assertDefined(this.user.userId);
    const { userId } = this.user;
    if (!this.settingsFormState) {
      return;
    }
    const presentation = state.socialEvent as Presentation;
    const updatingInteractionMode = this.settingsFormState.interactionMode !== presentation.settings?.interactionMode;

    // Cleaning queue, cleaning speaking participants and sending new settings
    // Only clean if user is changing the interaction mode
    const updatedProperties: Partial<Presentation> = {
      speakingParticipants: updatingInteractionMode ? {} : presentation.speakingParticipants,
      queuedToSpeakIds: updatingInteractionMode ? [] : presentation.queuedToSpeakIds,
      settings: this.settingsFormState,
    };
    if (!userId) {
      return;
    }
    await SocialStateMachine.shared
      .requestUpdatePresentation({
        updatedProperties,
        userId,
        updateType: this.isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
        operation: 'updateSettings',
      })
      .catch((e) => this.error('Error request update presentation', e));
  }

  public async submit(): Promise<void> {
    const eventProperties = {
      preset: this.presetSelected?.text ?? '',
      defaultFullScreen: this.settingsFormState?.defaultFullScreen,
      requireFullScreen: this.settingsFormState?.requireFullScreen,
      groupConvos: this.settingsFormState?.allowGroupConversations,
      audienceParticipation: this.settingsFormState?.interactionMode,
    };
    this.logUserAction('submit', 'click', { eventProperties });
    await this.updateSettings();
  }

  public get dirty(): boolean {
    return !isEqual(this.settingsFormState, this.presentationSettings);
  }

  public handleAdvancedSettingChange(newSettings: Partial<PresentationSettings>): void {
    this.settingsFormState = { ...(this.settingsFormState ?? {}), ...newSettings } as PresentationSettings;
  }

  private handleSettingsUpdate(settings: PresentationSettings | undefined) {
    // Preserve form state if it's dirty.
    if (!this.dirty) {
      this.settingsFormState = settings;
    }

    this.presentationSettings = settings;
  }
}
